import { useState } from 'react';

import {
  useCreateHoliday,
  useDeleteHoliday,
  useHolidays,
  type Holiday,
  type HolidayType,
} from '@frontend/api/hooks/leave';
import { Can } from '@frontend/auth/ProtectedRoute';
import { formatDateLong, humanise } from '@frontend/lib/format';
import { Button } from '@frontend/components/ui/Button';
import { DataTable, type Column } from '@frontend/components/ui/DataTable';
import { Checkbox, FieldShell, Input, Select } from '@frontend/components/ui/Field';
import { Modal } from '@frontend/components/ui/Modal';
import { PageHeader } from '@frontend/components/ui/Page';
import { EmptyState, ErrorState, LoadingRows } from '@frontend/components/ui/States';
import { useToast } from '@frontend/components/ui/Toast';

const HOLIDAY_TYPES: HolidayType[] = ['NATIONAL', 'REGIONAL', 'COMPANY', 'OPTIONAL'];

export function HolidaysPage(): JSX.Element {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const query = useHolidays(year);

  // The calendar could be read and never built: all three write routes had no caller. With no
  // holidays configured, leave counts exclude weekends and the eleven US federal holidays and
  // nothing else — so every company closure the federal calendar misses was quietly deducted
  // from people's balances, and the empty state told HR to "add the calendar" with no way to.
  const createHoliday = useCreateHoliday();
  const deleteHoliday = useDeleteHoliday();
  const toast = useToast();

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [type, setType] = useState<HolidayType>('COMPANY');
  const [isWorkingDay, setIsWorkingDay] = useState(false);

  const columns: Column<Holiday>[] = [
    {
      key: 'date',
      header: 'Date',
      cell: (row) => formatDateLong(row.date),
      sort: (a, b) => a.date.localeCompare(b.date),
    },
    { key: 'name', header: 'Holiday', cell: (row) => <span className="font-medium text-content">{row.name}</span> },
    { key: 'type', header: 'Type', cell: (row) => humanise(row.type), hideOnMobile: true },
    {
      key: 'scope',
      header: 'Applies to',
      hideOnMobile: true,
      cell: (row) => (row.state === null || row.state === undefined ? 'All locations' : `${row.state} only`),
    },
    {
      key: 'optional',
      header: '',
      cell: (row) =>
        row.isOptional === true ? (
          <span className="rounded-sm bg-surface-muted px-1.5 py-0.5 text-body text-content-secondary">
            Optional
          </span>
        ) : null,
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <Can permission="leave.holiday.manage">
          <span className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              data-testid={`holiday-remove-${row.id}`}
              onClick={() => {
                deleteHoliday.mutate(row.id, {
                  onSuccess: () =>
                    toast.success(`${row.name} removed. Leave taken across that date now counts it as a working day.`),
                  onError: (error: unknown) =>
                    toast.error(error instanceof Error ? error.message : String(error)),
                });
              }}
            >
              Remove
            </Button>
          </span>
        </Can>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Company holidays"
        description="Public and company holidays are excluded from leave day counts automatically — you do not need to work around them when requesting leave."
        actions={
          <>
            <Select
              aria-label="Year"
              value={String(year)}
              onChange={(event) => setYear(Number(event.target.value))}
              className="w-[110px]"
            >
              {[currentYear - 1, currentYear, currentYear + 1].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
            <Can permission="leave.holiday.manage">
              <Button
                variant="primary"
                data-testid="add-holiday"
                onClick={() => {
                  setName('');
                  setDate(`${year}-01-01`);
                  setType('COMPANY');
                  setIsWorkingDay(false);
                  setAdding(true);
                }}
              >
                Add holiday
              </Button>
            </Can>
          </>
        }
      />

      {query.isLoading ? (
        <LoadingRows rows={8} columns={4} />
      ) : query.isError ? (
        <ErrorState
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : (
        <div className="surface-card">
          <DataTable
            rows={query.data?.holidays ?? []}
            columns={columns}
            rowKey={(row) => row.id}
            testIdPrefix="holiday-row"
            emptyState={
              <EmptyState
                title={`No holidays are set up for ${year}`}
                description="Until the calendar is loaded, leave requests count every weekday as a working day — including company closures like the Friday after Thanksgiving. Add them with the button above."
              />
            }
          />
        </div>
      )}

      <Modal open={adding} onClose={() => setAdding(false)} title={`Add a holiday to ${year}`} data-testid="holiday-modal">
        <div className="flex flex-col gap-4">
          <FieldShell label="Name" htmlFor="hol-name" required fieldKey="hol-name">
            <Input
              id="hol-name"
              name="holName"
              rawName
              value={name}
              placeholder="Day after Thanksgiving"
              onChange={(event) => setName(event.target.value)}
              data-testid="hol-name"
            />
          </FieldShell>

          <FieldShell label="Date" htmlFor="hol-date" required fieldKey="hol-date">
            <Input
              id="hol-date"
              name="holDate"
              rawName
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              data-testid="hol-date"
            />
          </FieldShell>

          <FieldShell
            label="Type"
            htmlFor="hol-type"
            fieldKey="hol-type"
            hint="Company covers a closure this business observes. Optional is a day people may take but need not."
          >
            <Select
              id="hol-type"
              name="holType"
              autoComplete="off"
              value={type}
              onChange={(event) => setType(event.target.value as HolidayType)}
              data-testid="hol-type"
            >
              {HOLIDAY_TYPES.map((option) => (
                <option key={option} value={option}>
                  {humanise(option)}
                </option>
              ))}
            </Select>
          </FieldShell>

          <Checkbox
            label="This is a working day"
            checked={isWorkingDay}
            onChange={(event) => setIsWorkingDay(event.target.checked)}
            data-testid="hol-working"
          />

          <div className="flex justify-end gap-2 border-t border-line pt-3">
            <Button variant="secondary" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={name.trim().length < 2 || date === ''}
              isLoading={createHoliday.isPending}
              data-testid="hol-save"
              onClick={() => {
                createHoliday.mutate(
                  { name: name.trim(), date: date as Holiday['date'], type, isWorkingDay },
                  {
                    onSuccess: () => {
                      toast.success(`${name.trim()} added. Leave taken across it no longer counts that day.`);
                      setAdding(false);
                    },
                    onError: (error: unknown) =>
                      toast.error(error instanceof Error ? error.message : String(error)),
                  },
                );
              }}
            >
              Add holiday
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
